import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { savedSubjects } from '@/mocks/subjects';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';

type SortMode = 'default' | 'oldest' | 'az';

interface StudyMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const sortLabels: Record<SortMode, string> = {
  default: 'Default',
  oldest: 'Oldest to Newest',
  az: 'A – Z',
};

function getSortedSubjects(sort: SortMode) {
  const subjects = [...savedSubjects];
  if (sort === 'oldest') {
    subjects.sort((a, b) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime());
  } else if (sort === 'az') {
    subjects.sort((a, b) => a.name.localeCompare(b.name));
  }
  return subjects;
}

export default function StudyMenu({ isOpen, onClose }: StudyMenuProps) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [sortMode, setSortMode] = useState<SortMode>('default');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const allSubjects = getSortedSubjects(sortMode);
  const subjects = searchQuery.trim()
    ? allSubjects.filter((s) =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.shortName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allSubjects;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowSortDropdown(false);
      }
    }
    if (showSortDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSortDropdown]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 150);
    }
  }, [showSearch]);

  useEffect(() => {
    if (!isOpen) {
      setShowSearch(false);
      setSearchQuery('');
      setShowSortDropdown(false);
    }
  }, [isOpen]);

  const handleSubjectClick = (id: string) => {
    navigate(`/subject/${id}`);
    onClose();
  };

  const handleSearchToggle = () => {
    if (showSearch) {
      setShowSearch(false);
      setSearchQuery('');
    } else {
      setShowSearch(true);
    }
  };

  // Theme tokens
  const bg = isDark ? 'bg-[#0F0F10]' : 'bg-white';
  const textPrimary = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-[#A1A1A6]' : 'text-neutral-400';
  const cardBg = isDark ? 'bg-[#1C1D21]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/[0.05]' : 'border-neutral-100/80';
  const cardShadow = isDark ? 'shadow-none' : 'shadow-[0_2px_8px_rgba(0,0,0,0.04)]';
  const dropdownBg = isDark ? 'bg-[#1C1D21]' : 'bg-white';
  const dropdownBorder = isDark ? 'border-white/[0.08]' : 'border-neutral-200/80';
  const dropdownShadow = isDark ? 'shadow-[0_12px_40px_-8px_rgba(0,0,0,0.6)]' : 'shadow-[0_12px_40px_-8px_rgba(0,0,0,0.12)]';
  const activeSortBg = isDark ? 'bg-white/[0.07]' : 'bg-neutral-100';
  const dividerColor = isDark ? 'border-white/[0.05]' : 'border-neutral-100/80';
  const closeIconColor = isDark ? 'text-[#6E6E73]' : 'text-neutral-400';
  const searchActiveBg = isDark ? 'bg-white/[0.07] border-white/[0.12]' : 'bg-neutral-50 border-neutral-200';
  const searchBtnActive = isDark ? 'bg-white/[0.1] border-white/[0.15]' : 'bg-neutral-100 border-neutral-200';

  const sortOptions: SortMode[] = ['default', 'oldest', 'az'];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`fixed inset-0 z-50 flex flex-col ${bg}`}
        >
          {/* ── Header ── */}
          <div className="flex-shrink-0 px-5 pt-6 pb-3">
            <div className="flex items-center justify-between mb-3">
              <h1 className={`text-xl font-bold ${textPrimary}`}>Study</h1>

              <div className="flex items-center gap-2">
                {/* Search toggle */}
                <Pressable
                  onClick={handleSearchToggle}
                  className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-all duration-200 ${
                    showSearch
                      ? searchBtnActive
                      : `${cardBg} ${cardBorder} ${cardShadow}`
                  }`}
                  scale={0.9}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${showSearch ? 'ri-close-line' : 'ri-search-line'} text-sm ${closeIconColor} transition-all duration-200`} />
                  </div>
                </Pressable>

                {/* Sort menu */}
                <div className="relative" ref={dropdownRef}>
                  <Pressable
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl ${cardBg} border ${cardBorder} ${cardShadow} transition-colors duration-500`}
                    scale={0.95}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-sort-asc text-sm ${closeIconColor} transition-colors duration-500`} />
                    </div>
                    <span className={`text-xs font-medium ${textSecondary} transition-colors duration-500`}>
                      {sortLabels[sortMode]}
                    </span>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line text-xs ${closeIconColor} transition-colors duration-500`} />
                    </div>
                  </Pressable>

                  <AnimatePresence>
                    {showSortDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.96 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className={`absolute right-0 top-full mt-2 w-48 ${dropdownBg} rounded-xl border ${dropdownBorder} ${dropdownShadow} overflow-hidden z-50`}
                      >
                        {sortOptions.map((mode, index) => (
                          <button
                            key={mode}
                            onClick={() => { setSortMode(mode); setShowSortDropdown(false); }}
                            className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center justify-between transition-colors duration-200 ${
                              sortMode === mode
                                ? `${activeSortBg} ${textPrimary}`
                                : textSecondary
                            } ${index !== sortOptions.length - 1 ? `border-b ${dividerColor}` : ''}`}
                          >
                            <span>{sortLabels[mode]}</span>
                            {sortMode === mode && (
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className={`ri-check-line text-sm ${isDark ? 'text-white' : 'text-neutral-900'}`} />
                              </div>
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Close */}
                <Pressable
                  onClick={onClose}
                  className={`w-9 h-9 rounded-xl ${cardBg} border ${cardBorder} ${cardShadow} flex items-center justify-center transition-colors duration-500`}
                  scale={0.9}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-close-line text-sm ${closeIconColor} transition-colors duration-500`} />
                  </div>
                </Pressable>
              </div>
            </div>

            {/* ── Animated Search Bar ── */}
            <AnimatePresence>
              {showSearch && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -6 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                  className="overflow-hidden"
                >
                  <div className={`flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 border mb-1 transition-colors duration-300 ${searchActiveBg}`}>
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-search-line text-sm ${closeIconColor}`} />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search subjects..."
                      className={`flex-1 bg-transparent text-sm font-medium outline-none placeholder:font-normal ${textPrimary} ${
                        isDark ? 'placeholder:text-neutral-600' : 'placeholder:text-neutral-400'
                      }`}
                    />
                    {searchQuery.length > 0 && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/[0.1]' : 'bg-neutral-200'}`}
                      >
                        <i className={`ri-close-line text-[10px] ${closeIconColor}`} />
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Subjects Grid ── */}
          <div className="flex-1 overflow-y-auto px-5 pb-8">
            <AnimatePresence mode="wait">
              {subjects.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <div className={`w-14 h-14 rounded-2xl ${isDark ? 'bg-white/[0.04]' : 'bg-neutral-100'} flex items-center justify-center mb-4`}>
                    <i className={`ri-search-line text-2xl ${closeIconColor}`} />
                  </div>
                  <p className={`text-sm font-semibold ${textPrimary}`}>No subjects found</p>
                  <p className={`text-xs ${textSecondary} mt-1`}>Try a different search term</p>
                </motion.div>
              ) : (
                <motion.div
                  key={searchQuery + sortMode}
                  className="grid grid-cols-2 gap-3"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: { staggerChildren: 0.03, delayChildren: 0.02 },
                    },
                  }}
                >
                  {subjects.map((subject) => (
                    <motion.div
                      key={subject.id}
                      variants={{
                        hidden: { opacity: 0, y: 12, scale: 0.97 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: { type: 'spring', stiffness: 400, damping: 30 },
                        },
                      }}
                      layout
                    >
                      <Pressable
                        onClick={() => handleSubjectClick(subject.id)}
                        className={`${cardBg} rounded-2xl border ${cardBorder} ${cardShadow} p-4 w-full flex flex-col items-start gap-3 transition-colors duration-500`}
                        scale={0.96}
                      >
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: isDark ? '#444448' : '#747478' }}
                        >
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className={`${subject.icon} text-white text-xl`} />
                          </div>
                        </div>
                        <div className="min-w-0 w-full">
                          <h3 className={`text-sm font-semibold ${textPrimary} leading-tight transition-colors duration-500`}>
                            {subject.name}
                          </h3>
                          <p className={`text-[11px] ${textSecondary} mt-1 transition-colors duration-500`}>
                            {subject.scanCount} scans
                          </p>
                        </div>
                      </Pressable>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
